/**
 * Monthly drug-screen obligation tracking.
 *
 * BHG holds the patient responsible for getting a screen each month, but the
 * clinic only schedules them at random. A patient who is never called still has
 * to show up. These helpers turn "no screen on record this month" into a nudge
 * that stays quiet early in the month and escalates as the deadline closes.
 */

/** Days remaining in the month at which the soft nudge starts appearing. */
export const MONTHLY_SCREEN_WARNING_DAYS = 7;

/** Days remaining at which the nudge escalates to urgent. */
export const MONTHLY_SCREEN_URGENT_DAYS = 3;

export type MonthlyScreenUrgency = "none" | "warning" | "urgent";

/**
 * What, if anything, accounts for this month's screen.
 *
 * Kept distinct because the two are not equally strong: "clinic" means the
 * clinic itself announced the screen on a recorded call, while "self-report" is
 * the patient's own dated note. Both satisfy the obligation as far as the nudge
 * is concerned, but the UI must not present them as the same kind of fact.
 */
export type MonthlyScreenSatisfiedBy = "none" | "clinic" | "self-report";

export type MonthlyScreenStatus = {
  /** `YYYY-MM` the status was computed for. */
  monthPrefix: string;
  /** Clinic-confirmed screens on record for this month. */
  screenCount: number;
  /** Screens the patient recorded themselves for this month. */
  selfReportedCount: number;
  /** Days left in the month, counting today. The last day of the month is 1. */
  daysRemaining: number;
  /** Whether the obligation is satisfied by either kind of record. */
  satisfied: boolean;
  /** Which kind of record satisfied it; "clinic" wins when both exist. */
  satisfiedBy: MonthlyScreenSatisfiedBy;
  urgency: MonthlyScreenUrgency;
};

/** Number of days in the month containing the given ISO date. */
export function daysInIsoMonth(isoDate: string): number {
  const parts = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return 0;
  const year = Number(parts[1]);
  const month = Number(parts[2]);
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function computeMonthlyScreenStatus(input: {
  todayIsoDate: string;
  screenCount: number;
  /**
   * Screens the patient logged themselves. Optional so callers that predate
   * self-reporting keep working; absent is treated as zero.
   */
  selfReportedCount?: number;
}): MonthlyScreenStatus | null {
  const parts = input.todayIsoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return null;

  const monthPrefix = `${parts[1]}-${parts[2]}`;
  const dayOfMonth = Number(parts[3]);
  const totalDays = daysInIsoMonth(input.todayIsoDate);
  if (totalDays === 0 || dayOfMonth < 1 || dayOfMonth > totalDays) return null;

  const daysRemaining = totalDays - dayOfMonth + 1;
  const selfReportedCount = input.selfReportedCount ?? 0;

  // A self-report silences the nudge. It is weaker evidence than a clinic
  // confirmation, but continuing to tell someone to go get screened after they
  // told us they did is worse than trusting them: it trains them to ignore us.
  const satisfiedBy: MonthlyScreenSatisfiedBy =
    input.screenCount > 0 ? "clinic" : selfReportedCount > 0 ? "self-report" : "none";
  const satisfied = satisfiedBy !== "none";

  const urgency: MonthlyScreenUrgency = satisfied
    ? "none"
    : daysRemaining <= MONTHLY_SCREEN_URGENT_DAYS
      ? "urgent"
      : daysRemaining <= MONTHLY_SCREEN_WARNING_DAYS
        ? "warning"
        : "none";

  return {
    monthPrefix,
    screenCount: input.screenCount,
    selfReportedCount,
    daysRemaining,
    satisfied,
    satisfiedBy,
    urgency,
  };
}

/** Strip the stored `bhg:` prefix and case/space differences off a rule label. */
function normalizeScreenLabel(value: string): string {
  return value.trim().toLowerCase().replace(/^bhg:\s*/, "");
}

/** `YYYY-MM` of an ISO date, or null when it is missing or malformed. */
function monthPrefixOf(isoDate: string | null | undefined): string | null {
  if (!isoDate) return null;
  const parts = isoDate.match(/^(\d{4})-(\d{2})-\d{2}$/);
  return parts ? `${parts[1]}-${parts[2]}` : null;
}

/**
 * Whether a day's scheduled labels include something that actually satisfies the
 * monthly screen obligation.
 *
 * `countingLabels` is the set of rule labels flagged `countsTowardMonthlyScreen`.
 * When it is absent the caller does not know the configuration, and we fall back
 * to treating any scheduled visit as satisfying — the historical behaviour. That
 * errs toward silence, which is the right direction for an unknown: this copy is
 * heavy, and sending it to someone who is in fact scheduled is worse than
 * missing a day.
 */
function dayHasQualifyingScreen(
  scheduledLabels: string[],
  countingLabels: string[] | undefined
): boolean {
  if (scheduledLabels.length === 0) return false;
  if (countingLabels === undefined) return true;
  if (countingLabels.length === 0) return false;
  const counting = new Set(countingLabels.map(normalizeScreenLabel).filter(Boolean));
  return scheduledLabels.some((label) => counting.has(normalizeScreenLabel(label)));
}

/**
 * The urgency the monthly-screen nudge should actually be delivered at, after
 * accounting for the schedule. `"none"` means stay quiet.
 *
 * Shared by the SMS/email templates and the dashboard so the two cannot drift:
 * a patient who sees the nudge on the dashboard must be the same patient who
 * gets it in a message.
 *
 * The suppression rule is "don't nag someone about a screen they're already
 * booked for", with two qualifications that are easy to miss:
 *
 *  - Tomorrow only counts if tomorrow is in the *same month*. On July 31 an
 *    August 1 screen satisfies August; July still closes tonight with nothing
 *    on record. That is the single most important day to speak up, and the
 *    naive check silenced exactly it.
 *  - Only a visit that counts toward the obligation qualifies. A bottle check
 *    tomorrow is not a screen and must not buy silence.
 */
export function resolveMonthlyScreenNudgeUrgency(input: {
  monthlyScreen: MonthlyScreenStatus | null | undefined;
  todayScheduledLabels: string[];
  tomorrowScheduledLabels: string[];
  /** Tomorrow's calendar date, used only to test whether it is still this month. */
  tomorrowIsoDate?: string | null;
  /** Rule labels flagged `countsTowardMonthlyScreen`; omit when unknown. */
  monthlyScreenLabels?: string[];
}): MonthlyScreenUrgency {
  const status = input.monthlyScreen;
  if (!status || status.urgency === "none") return "none";

  // Today is in the current month by construction — no date check needed.
  if (dayHasQualifyingScreen(input.todayScheduledLabels, input.monthlyScreenLabels)) {
    return "none";
  }

  // An absent or unparseable tomorrow date cannot be shown to be in this month,
  // so it does not earn silence.
  const tomorrowMonth = monthPrefixOf(input.tomorrowIsoDate);
  if (
    tomorrowMonth === status.monthPrefix &&
    dayHasQualifyingScreen(input.tomorrowScheduledLabels, input.monthlyScreenLabels)
  ) {
    return "none";
  }

  return status.urgency;
}

/**
 * User-facing nudge copy. Returns null when there is nothing to say — either the
 * obligation is met or the deadline is still far enough out.
 *
 * Deliberately avoids naming the clinic or the treatment: this copy reaches SMS
 * and email, which are not private channels.
 */
export function buildMonthlyScreenMessage(status: MonthlyScreenStatus | null): string | null {
  if (!status || status.urgency === "none") return null;

  const dayLabel = status.daysRemaining === 1 ? "today" : `in ${status.daysRemaining} days`;

  if (status.urgency === "urgent") {
    return `You have no drug screen on record this month and the month ends ${dayLabel}. Getting screened is your responsibility even if you were never scheduled — contact the clinic today, and go in before the month ends regardless.`;
  }

  return `You have no drug screen on record this month, and the month ends ${dayLabel}. Getting screened is your responsibility even if you were never scheduled — consider contacting the clinic to arrange one.`;
}

/** Short form for headline/subject slots. */
export function buildMonthlyScreenHeadline(status: MonthlyScreenStatus | null): string | null {
  if (!status || status.urgency === "none") return null;
  return status.daysRemaining === 1
    ? "No drug screen this month — last day"
    : `No drug screen this month — ${status.daysRemaining} days left`;
}
